import React from 'react'
import {
  Stack,
  Divider,
  TextInput,
  Switch,
  Button,
  Group,
  Progress,
  Table,
} from '@mantine/core'
import { DataPoint, useMetrics } from '../api'
import { useWidgetContext } from '../providers'
import { WidgetWrapper } from '../WidgetWrapper'
import { z } from 'zod'
import { useWidgetEditor } from '../state'
import { KpiListOptions } from '../schemas'
import classes from './KpiTabsContainer.module.css'
import { FmtColored, useFmtColor } from '../format'
import { Typography } from '../../common/typography'
import StatRow from '../../stories/StatRow/StatRow'

type OptionsType = z.infer<typeof KpiListOptions>

export default function KpiList(props: { id: string }) {
  const ctx = useWidgetContext()
  const editor = useWidgetEditor()
  const config = editor.useConfig(props.id)
  const { data: metrics } = useMetrics()

  if (config == null || config.widget !== 'KpiList') {
    console.error(
      `Invalid widget config for KpiList widget ${props.id}`,
      config
    )
    return null
  }

  const options: OptionsType = config.options

  // Filter to only KPI metrics if showOnlyKpis is true
  // Don't show percent based metrics as they don't have 100% target like others
  const kpiMetrics = options.showOnlyKpis
    ? metrics.filter((m) => m.is_kpi)
    : metrics

  // Get all metric keys for the query
  const metricKeys = kpiMetrics.map((m) => m.key)

  return (
    <WidgetWrapper
      id={props.id}
      transformData={(data: DataPoint[]) => {
        // Create a map of metric id to percent value
        const metricValues = new Map<number, number>()
        data.forEach((point) => {
          if (point.metric && point.value !== null) {
            metricValues.set(point.metric, point.value)
          }
        })
        return { metricValues, data }
      }}
      render={({ metricValues }) => (
        <Table
          verticalSpacing="xxs"
          horizontalSpacing="xxs"
          className={classes.kpi_table}
          withRowBorders={false}
        >
          <Table.Tbody>
            {kpiMetrics.map((metric) => {
              const value = metricValues.get(metric.metric_id)
              return (
                <KpiRow
                  key={metric.key}
                  metric={metric.key}
                  title={
                    options.useShortNames ? metric.short_name : metric.name
                  }
                  value={value}
                />
              )
            })}
          </Table.Tbody>
        </Table>
      )}
      query={() => ({
        period_type: ctx.period_type || 'total',
        period_range: false,
        periods: [ctx.period || '2025-01-01'],
        subject_type: ctx.subject_type || 'user',
        subject_ids: [ctx.subject || 1],
        metrics: metricKeys,
        components: metricKeys.map(() => 'percent'),
        statistics: metricKeys.map(() => 'value'),
        competition_id: ctx.competition_id,
      })}
    />
  )
}

function KpiRow({
  title,
  value,
  metric,
}: {
  title: string
  value: number | null | undefined
  metric?: string | number
}) {
  metric = metric || ''
  const color = useFmtColor({
    metric,
    value,
    component: 'percent',
    statistic: 'value',
  })

  return (
    <Table.Tr>
      <Table.Td>
        <Typography fc={{ light: 'black', dark: 'gray-2' }} lh="110">
          {title}
        </Typography>
      </Table.Td>
      <Table.Td>
        <Progress color={color} value={(value || 0) * 100} />
      </Table.Td>
      <Table.Td>
        <FmtColored
          value={value}
          metric={metric}
          component="percent"
          statistic="value"
          override={{ percent_decimal_places: 1 }}
          className={classes.statValue}
        />
      </Table.Td>
    </Table.Tr>
  )
}

// Form component for KpiList options
export function KpiListForm({ id }: { id: string }) {
  const editor = useWidgetEditor()
  const isSaving = editor.useIsSaving()
  const config = editor.useConfig(id)
  const [formValues, setFormValues] = React.useState(config.options)

  if (config.widget !== 'KpiList')
    throw new Error('Invalid widget type, expected KpiList')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    editor.setOptions(formValues)
    editor.save()
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="sm">
        <Divider label="KPI List Options" />
        <TextInput
          label="Title"
          value={formValues.title || ''}
          onChange={(e) =>
            setFormValues({ ...formValues, title: e.currentTarget.value })
          }
        />
        <Switch
          label="Show only KPI metrics"
          checked={formValues.showOnlyKpis ?? true}
          onChange={(e) =>
            setFormValues({
              ...formValues,
              showOnlyKpis: e.currentTarget.checked,
            })
          }
        />
        <Switch
          label="Use short names"
          checked={formValues.useShortNames ?? false}
          onChange={(e) =>
            setFormValues({
              ...formValues,
              useShortNames: e.currentTarget.checked,
            })
          }
        />
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button color="red.4" variant="outline" onClick={editor.endEditWidget}>
          Cancel
        </Button>
        <Button type="submit" loading={isSaving}>
          Save
        </Button>
      </Group>
    </form>
  )
}

export const KpiListRegistry = {
  component: KpiList,
  formComponent: KpiListForm,
  mock: () => (
    <Stack
      gap="xs"
      p="md"
      style={{ backgroundColor: 'white', borderRadius: '8px' }}
    >
      <StatRow title="Sample KPI 1" value={85} />
      <StatRow title="Sample KPI 2" value={67} />
      <StatRow title="Sample KPI 3" value={45} />
    </Stack>
  ),
  getInitialOptions: () => KpiListOptions.parse({}),
}
